/**
 * Channels API client.
 *
 * Validates Discord bot tokens and fetches guild/channel metadata.
 * Both endpoints take the token in the POST body — never a query string.
 *
 * This file carries two parallel shape sets:
 *   1. Legacy (`DiscordValidateResponse`, `DiscordChannel`, `DiscordGuild`,
 *      `DiscordGuildsResponse`, `channelsApi`): consumed by the pre-v2
 *      `ChannelsForm` and the legacy wizard paths.
 *   2. v2 (`DiscordBotInfo`, `DiscordChannelV2`, `DiscordGuildV2`,
 *      `validateDiscordToken`, `listDiscordGuilds`): consumed by the v2
 *      DiscordChannelPicker + NewAgentWizard. Uses the numeric type codes
 *      returned by the Discord REST API directly so the picker can filter
 *      text (0) and announcement (5) channels without a server-side rename.
 *
 * Both sets hit the same backend endpoints; the backend response payload
 * is assumed to satisfy both projections. Keep them separated so that the
 * legacy caller migration is a follow-up, not a hard cut-over.
 */
import { api } from '@/api/client';

// ---------- Legacy (pre-v2) shapes — kept for ChannelsForm compatibility.

export interface DiscordValidateResponse {
  valid: boolean;
  bot_name?: string;
  bot_id?: string;
  bot_avatar_url?: string;
  error?: string;
}

export interface DiscordChannel {
  id: string;
  name: string;
  type: 'text' | 'voice' | 'category' | 'thread' | 'dm' | 'other';
  parent_id?: string;
}

export interface DiscordGuild {
  id: string;
  name: string;
  icon_url?: string;
  channels: DiscordChannel[];
}

export interface DiscordGuildsResponse {
  guilds: DiscordGuild[];
}

export const channelsApi = {
  async validateDiscord(token: string): Promise<DiscordValidateResponse> {
    const raw = await api<DiscordBotInfo>('/channels/discord/validate', {
      method: 'POST',
      body: { token },
    });
    return {
      valid: true,
      bot_name: raw.username
        ? `${raw.username}#${raw.discriminator ?? '0'}`
        : undefined,
      bot_id: raw.id,
      bot_avatar_url: raw.avatar
        ? `https://cdn.discordapp.com/avatars/${raw.id}/${raw.avatar}.png`
        : undefined,
    };
  },

  async listDiscordGuilds(token: string): Promise<DiscordGuildsResponse> {
    const raw = await api<DiscordGuildV2[]>('/channels/discord/guilds', {
      method: 'POST',
      body: { token },
    });
    const TYPE_MAP: Record<number, DiscordChannel['type']> = {
      0: 'text', 2: 'voice', 4: 'category', 5: 'text', 11: 'thread', 12: 'thread',
    };
    return {
      guilds: (raw ?? []).map((g) => ({
        id: g.id,
        name: g.name,
        icon_url: g.icon
          ? `https://cdn.discordapp.com/icons/${g.id}/${g.icon}.png`
          : undefined,
        channels: (g.channels ?? []).map((ch) => ({
          id: ch.id,
          name: ch.name,
          type: TYPE_MAP[ch.type] ?? 'other',
          parent_id: ch.parent_id ?? undefined,
        })),
      })),
    };
  },
};

// ---------- v2 shapes (used by DiscordChannelPicker + NewAgentWizard).

/** v2 bot info returned by POST /channels/discord/validate. */
export interface DiscordBotInfo {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
  /** Number of guilds the bot can see. Optional on older backends. */
  guild_count?: number;
}

/** v2 channel — uses Discord's native numeric type code. */
export interface DiscordChannelV2 {
  id: string;
  name: string;
  /** Discord channel type: 0 = text, 5 = announcement, 2 = voice, etc. */
  type: number;
  parent_id?: string | null;
}

/** v2 guild shape returned by POST /channels/discord/guilds. */
export interface DiscordGuildV2 {
  id: string;
  name: string;
  icon: string | null;
  channels: DiscordChannelV2[];
}

/**
 * Validate a Discord bot token against `/channels/discord/validate`.
 * Returns the bot identity so the caller can show "Connected as …".
 * Throws ApiError on non-2xx (e.g. invalid token).
 */
export async function validateDiscordToken(token: string): Promise<DiscordBotInfo> {
  return api<DiscordBotInfo>('/channels/discord/validate', {
    method: 'POST',
    body: { token },
  });
}

/**
 * Fetch the list of guilds + channels the bot can see. The backend takes
 * the token in the POST body so we never log it via a query string.
 */
export async function listDiscordGuilds(token: string): Promise<DiscordGuildV2[]> {
  return api<DiscordGuildV2[]>('/channels/discord/guilds', {
    method: 'POST',
    body: { token },
  });
}
